/**
 * 러너 스케줄 다중 대상(내 러너 여러 대) 선택에서 쓰는 순수 계산 모음.
 *
 * 스케줄 하나의 엔진·모델·effort·빠른 모드는 모든 대상 러너에 그대로 적용되므로,
 * 화면이 고를 수 있는 값은 선택한 러너 전부가 공통으로 지원하는 값(교집합)뿐이다.
 * 여기 규칙은 서버의 `validateRunnerScheduleTargets` 판정과 맞춘 사전 안내이며,
 * 최종 판정은 저장 요청에 대한 서버 400 응답이다.
 */

import { resolveEffectiveEngines } from './daemonEngines';

export type RunnerScheduleTargetDaemon = {
  id: string;
  label?: string | null;
  supportedEngines?: readonly string[] | null;
  detectedEngines?: readonly string[] | null;
  detectedEnginesAt?: string | null;
};

export type RunnerScheduleTargetAgent = {
  id: string;
  name: string;
  targetDaemonId?: string | null;
};

export type RunnerScheduleTargetModelOption = {
  value: string;
  label: string;
  fastModeSupported?: boolean;
  supportedEffortLevels?: readonly string[];
};

export type RunnerScheduleTargetSelection = {
  daemonId: string;
  agentConfigId: string;
};

export type RunnerScheduleTargetRow = {
  daemon: RunnerScheduleTargetDaemon;
  agents: RunnerScheduleTargetAgent[];
};

/**
 * 선택한 러너 전부가 지원하는 엔진만 남긴다.
 * 러너마다 수동 지정이 있으면 그것을, 없으면 신선한 자동 탐지 목록을 쓴다
 * (서버 `resolveEffectiveEngines`와 같은 순서). 유효 목록이 빈 러너는 서버
 * 규칙상 엔진 제한이 없다는 뜻이므로 교집합 계산에서 제외한다.
 * 계산에 기여한 러너가 하나도 없으면 제한 없음으로 보고 전체 엔진을 돌려준다.
 * 전체 목록을 모르면 빈 목록을 돌려줘 이전 동작과 같다.
 */
export const intersectEngines = (
  daemons: readonly RunnerScheduleTargetDaemon[],
  options?: { allEngines?: readonly string[]; now?: number },
): string[] => {
  const now = options?.now ?? Date.now();
  const lists = daemons
    .map((daemon) =>
      resolveEffectiveEngines(
        {
          supportedEngines: daemon.supportedEngines ?? [],
          detectedEngines: daemon.detectedEngines ?? null,
          detectedEnginesAt: daemon.detectedEnginesAt ?? null,
        },
        now,
      ),
    )
    .filter((list) => list.length > 0);
  const [first, ...rest] = lists;
  if (!first) return [...(options?.allEngines ?? [])];
  return first.filter((engine) => rest.every((list) => list.includes(engine)));
};

/** 러너별 effort 레벨 목록의 교집합. 한 러너라도 지원하지 않는 레벨은 남기지 않는다. */
export const intersectEffortLevels = (lists: readonly (readonly string[] | undefined)[]): string[] => {
  const [first, ...rest] = lists.map((list) => list ?? []);
  if (!first) return [];
  return first.filter((level) => rest.every((list) => list.includes(level)));
};

/**
 * 러너별 모델 목록의 교집합. 모든 러너에 있는 모델만 남기고,
 * 그 모델의 effort 레벨과 빠른 모드 지원 여부도 러너 전부의 교집합으로 좁힌다.
 * 라벨은 첫 러너의 값을 쓴다(같은 모델 value는 카탈로그가 같아 라벨도 같다).
 */
export const intersectModelOptions = (
  lists: readonly (readonly RunnerScheduleTargetModelOption[])[],
): RunnerScheduleTargetModelOption[] => {
  const [first, ...rest] = lists;
  if (!first) return [];
  return first
    .filter((option) => rest.every((list) => list.some((item) => item.value === option.value)))
    .map((option) => {
      const perDaemon = lists.map((list) => list.find((item) => item.value === option.value));
      return {
        value: option.value,
        label: option.label,
        fastModeSupported: perDaemon.every((item) => item?.fastModeSupported === true),
        supportedEffortLevels: intersectEffortLevels(perDaemon.map((item) => item?.supportedEffortLevels)),
      };
    });
};

/** 내 러너마다 그 러너에 바인딩된 에이전트를 묶는다. 에이전트가 없는 러너는 대상으로 고를 수 없다. */
export const buildTargetRows = (
  daemons: readonly RunnerScheduleTargetDaemon[],
  agents: readonly RunnerScheduleTargetAgent[],
): RunnerScheduleTargetRow[] =>
  daemons.map((daemon) => ({
    daemon,
    agents: agents.filter((agent) => agent.targetDaemonId === daemon.id),
  }));

/**
 * 전체 선택 결과. 에이전트가 바인딩된 러너만 고르고,
 * 이미 고른 에이전트가 있으면 그대로 두어 사용자의 선택을 덮어쓰지 않는다.
 */
export const selectAllTargets = (
  rows: readonly RunnerScheduleTargetRow[],
  current: readonly RunnerScheduleTargetSelection[],
): RunnerScheduleTargetSelection[] =>
  rows.flatMap((row) => {
    const fallback = row.agents[0];
    if (!fallback) return [];
    return [
      {
        daemonId: row.daemon.id,
        agentConfigId: current.find((target) => target.daemonId === row.daemon.id)?.agentConfigId ?? fallback.id,
      },
    ];
  });
